// Formatting for the values a Betalingsservice file carries. Amounts are øre;
// dates are ddmmyy or ddmmyyyy with `000000` meaning "none".

const kroner = new Intl.NumberFormat("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Field names that hold an amount in øre. */
const AMOUNT_FIELDS = new Set(["amount", "paymentAmount"]);
/** Field names that hold a date. */
const DATE_FIELDS = new Set(["date", "endDate", "paymentDate", "bookkeepingDate"]);

export const isAmountField = (name: string): boolean => AMOUNT_FIELDS.has(name);
export const isDateField = (name: string): boolean => DATE_FIELDS.has(name);

/** Øre → "1.234,56 kr". */
export function formatAmount(oere: string | number): string {
  const value = typeof oere === "number" ? oere : Number(oere);
  if (!Number.isFinite(value)) return String(oere);
  return `${kroner.format(value / 100)} kr`;
}

/** ddmmyy / ddmmyyyy → "01.11.2021". `000000` and blanks read as "—". */
export function formatDate(value: string): string {
  const digits = value.trim();
  if (digits === "" || /^0+$/.test(digits)) return "—";
  if (/^\d{6}$/.test(digits)) return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.20${digits.slice(4, 6)}`;
  if (/^\d{8}$/.test(digits)) return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4, 8)}`;
  return digits;
}

/** The value of a decoded field, formatted for the field it is. */
export function formatFieldValue(name: string, value: string): string {
  if (value === "") return "—";
  if (isAmountField(name)) return formatAmount(value);
  if (isDateField(name)) return formatDate(value);
  return value;
}

const bytes = new Intl.NumberFormat("da-DK");

export function formatSize(size: number): string {
  return size < 1024 ? `${size} B` : `${bytes.format(Math.round(size / 1024))} kB`;
}

const timestamp = new Intl.DateTimeFormat("da-DK", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

export const formatTimestamp = (iso: string): string => timestamp.format(new Date(iso));

const shortTimestamp = new Intl.DateTimeFormat("da-DK", {
  day: "2-digit",
  month: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});

export const formatShortTimestamp = (iso: string): string => shortTimestamp.format(new Date(iso));

// Field names as the record layouts spell them → the words an operator uses.
// The layout names are the guidelines' names; these are the column headings you
// would write on a printout.
const FIELD_LABELS: Record<string, string> = {
  amount: "Amount",
  accountNumber: "Account no.",
  bookkeepingDate: "Booked",
  code: "Code",
  country: "Country",
  cprCvrNumber: "CPR/CVR",
  customerNumber: "Customer no.",
  date: "Date",
  dataRecordNumber: "Record no.",
  dataRecordType: "Record type",
  dataSupplierIdentification: "Supplier reference",
  dataSupplierNumber: "Data supplier",
  debtorCprCvrNumber: "CPR/CVR",
  debtorGroupNumber: "Debtor group",
  deliveryIdentification: "Delivery no.",
  deliveryType: "Delivery type",
  dispatchSpeed: "Dispatch",
  endDate: "End date",
  mainTextLine: "Main text",
  mandateNumber: "Mandate no.",
  mandatoryPrint: "Mandatory print",
  name: "Name",
  numberOfDataRecordType042: "042 records",
  numberOfRecordType022: "022 records",
  numberOfRecordType042: "042 records",
  numberOfRecordType052And062: "052/062 records",
  numberOfSections: "Sections",
  payerIdentification: "Payer id",
  paymentAmount: "Paid",
  paymentDate: "Paid on",
  pbsNumber: "PBS no.",
  postcode: "Postcode",
  reference: "Reference",
  registrationNumber: "Reg. no.",
  sectionNumber: "Section",
  signCode: "Sign",
  subsystem: "Subsystem",
  systemIdentification: "System",
  textLine: "Text",
  transactionCode: "Transaction",
};

export function fieldLabel(name: string): string {
  return (
    FIELD_LABELS[name] ??
    name
      .replace(/([A-Z])/g, " $1")
      .replace(/(\d+)/g, " $1")
      .replace(/^./, (c) => c.toUpperCase())
      .trim()
  );
}
